// utils/sitemapGenerator.js
import { apiEndpoints, fetchAPI } from "./api";

export class SitemapGenerator {
  constructor(baseUrl = "https://redarauca.com") {
    this.baseUrl = baseUrl;
  }

  // Generar URL completa
  generateUrl(path) {
    return `${this.baseUrl}${path}`;
  }

  // Formatear fecha para sitemap
  formatDate(date) {
    return new Date(date).toISOString().split("T")[0];
  }

  // Generar sitemap entry
  generateSitemapEntry(
    url,
    lastmod = null,
    changefreq = "weekly",
    priority = "0.8"
  ) {
    return {
      url,
      lastmod: lastmod ? this.formatDate(lastmod) : this.formatDate(new Date()),
      changefreq,
      priority,
    };
  }

  // Obtener URLs de posts
  async getPostUrls() {
    try {
      const response = await fetchAPI(apiEndpoints.posts.list(1, 1000)); // Obtener todos los posts
      const posts = response?.data || [];

      return posts.map((post) =>
        this.generateSitemapEntry(
          this.generateUrl(`/post/${post.slug}`),
          post.updated_at || post.created_at,
          "weekly",
          "0.9"
        )
      );
    } catch (error) {
      console.error("Error obteniendo URLs de posts:", error);
      return [];
    }
  }

  // Obtener URLs de categorías
  async getCategoryUrls() {
    try {
      const response = await fetchAPI(apiEndpoints.categories.list());
      const categories = response?.data || [];

      return categories.map((category) =>
        this.generateSitemapEntry(
          this.generateUrl(`/categoria/${category.slug}`),
          category.updated_at,
          "daily",
          "0.8"
        )
      );
    } catch (error) {
      console.error("Error obteniendo URLs de categorías:", error);
      return [];
    }
  }

  // Obtener URLs estáticas
  getStaticUrls() {
    const staticPages = [
      { path: "/", priority: "1.0", changefreq: "daily" },
      { path: "/noticias", priority: "0.9", changefreq: "daily" },
      { path: "/nosotros", priority: "0.7", changefreq: "monthly" },
      { path: "/contacto", priority: "0.6", changefreq: "monthly" },
      { path: "/miembros", priority: "0.8", changefreq: "weekly" },
    ];

    return staticPages.map((page) =>
      this.generateSitemapEntry(
        this.generateUrl(page.path),
        null,
        page.changefreq,
        page.priority
      )
    );
  }

  // Generar sitemap completo
  async generateSitemap() {
    try {
      const [staticUrls, postUrls, categoryUrls] = await Promise.all([
        this.getStaticUrls(),
        this.getPostUrls(),
        this.getCategoryUrls(),
      ]);

      const allUrls = [...staticUrls, ...postUrls, ...categoryUrls];

      return this.generateSitemapXML(allUrls);
    } catch (error) {
      console.error("Error generando sitemap:", error);
      return null;
    }
  }

  // Generar XML del sitemap
  generateSitemapXML(urls) {
    const urlElements = urls
      .map(
        (entry) => `
  <url>
    <loc>${entry.url}</loc>
    <lastmod>${entry.lastmod}</lastmod>
    <changefreq>${entry.changefreq}</changefreq>
    <priority>${entry.priority}</priority>
  </url>`
      )
      .join("");

    return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${urlElements}
</urlset>`;
  }

  // Generar robots.txt
  generateRobotsTxt() {
    return `User-agent: *
Allow: /

# Sitemaps
Sitemap: ${this.baseUrl}/sitemap.xml

# Optimizaciones
Crawl-delay: 1

# Bloquear archivos no necesarios para SEO
Disallow: /api/
Disallow: /admin/
Disallow: /*.json
Disallow: /*?*
Disallow: /search?*

# Permitir motores de búsqueda importantes
User-agent: Googlebot
Allow: /

User-agent: Bingbot
Allow: /

User-agent: facebookexternalhit
Allow: /

User-agent: Twitterbot
Allow: /`;
  }
}

// Hook para usar el generador de sitemap
export const useSitemap = () => {
  const generator = new SitemapGenerator();

  const generateAndDownloadSitemap = async () => {
    const sitemapXML = await generator.generateSitemap();
    if (sitemapXML) {
      const blob = new Blob([sitemapXML], { type: "application/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sitemap.xml";
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const generateAndDownloadRobots = () => {
    const robotsTxt = generator.generateRobotsTxt();
    const blob = new Blob([robotsTxt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "robots.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return {
    generateAndDownloadSitemap,
    generateAndDownloadRobots,
    generator,
  };
};
